import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { List, Map } from 'immutable';
import { Observable, concat, of as observableOf } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  withLatestFrom,
} from 'rxjs/operators';

import { State } from '®app/ngrx/state';
import {
  OnlineResource,
  OnlineResourceGoogleBooks,
  OnlineResourceUrl,
  isOnlineResourcePreview,
} from '®shared/bibliographic-resource/online-resource';
import {
  ConfirmCreateResourceItemAction,
  ContainerActionTypes,
  ContainerEditActionTypes,
  ContainerInitialisedAction,
  selectContainer,
} from '®shared/container-structure/state';
import {
  ItemPageLoadedAction,
  ItemPageLoadingFailed,
  ItemPagesInitialisedAction,
} from '®shared/container-structure/state/pageable-items.actions';
import { DEFAULT_CITATION_STYLES } from '®shared/reading-list/citation-switcher';
import {
  CitationsByItem,
  ListItem,
  PAGEABLE_EMPTY_ITEMS,
  PageableItems,
  ReadingListService,
  Section,
} from '®shared/reading-list/models';
import { ReadingListDetails } from '®shared/reading-list/models/reading-list-details';

import {
  CitationActionTypes,
  CitationStyleChangedAction,
  CitationsLoadedAction,
} from './citations.actions';
import {
  ItemsLoadedAction,
  ListItemActionTypes,
  OnlineResourcesLoadedAction,
  RequestGoogleBooksAction,
} from './list-items.actions';
import { selectCitationsByStyle } from './list-items.state';
import { getPageOffsetFromError, getPageOffsets } from './pageable-items.utils';

function getNonGoogleResources(
  onlineResources: Map<boolean, List<OnlineResourceUrl>>,
): List<OnlineResourceUrl> {
  return onlineResources.get(false).toList();
}

function getGoogleResources(
  onlineResources: Map<boolean, List<OnlineResourceGoogleBooks>>,
): List<OnlineResourceGoogleBooks> {
  return onlineResources.get(true).toList();
}

function hasNonGoogleResources(
  onlineResources: Map<boolean, List<OnlineResourceUrl>>,
): boolean {
  return onlineResources.has(false);
}

function hasGoogleResources(
  onlineResources: Map<boolean, List<OnlineResourceGoogleBooks>>,
): boolean {
  return onlineResources.has(true);
}

@Injectable()
export class ListItemsEffects {
  constructor(
    private actions$: Actions,
    private readingListService: ReadingListService,
    private store: Store<State>,
  ) {}

  loadCitationsForStyle$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CitationActionTypes.CITATION_STYLE_CHANGED),
      map((action: CitationStyleChangedAction) => action.payload.styleId),
      filter(styleId => styleId !== DEFAULT_CITATION_STYLES.NONE.id),
      withLatestFrom(
        this.store.select(selectCitationsByStyle),
        this.store
          .select(selectContainer)
          .pipe(filter(container => (container ? true : false))),
        (styleId, citationsByStyle, container) => {
          return { styleId, citationsByStyle, container };
        },
      ),
      filter(({ styleId, citationsByStyle }) => {
        return !citationsByStyle.has(styleId);
      }),
      mergeMap(({ styleId, container }) => {
        const citationsByItemId$ =
          container.type === 'lists'
            ? this.getReadingListItemCitationsByItemId(
                container as ReadingListDetails,
                styleId,
              )
            : this.getSectionItemCitationsByItemId(
                container as Section,
                styleId,
              );
        return citationsByItemId$.pipe(
          distinctUntilChanged(),
          map(
            citationsByItemId => new CitationsLoadedAction(citationsByItemId),
          ),
        );
      }),
    ),
  );

  itemPagesActions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ContainerActionTypes.CONTAINER_INITIALISED),
      mergeMap((action: ContainerInitialisedAction) => {
        const { container } = action.payload;
        const itemPageReset$ = observableOf(
          new ItemPagesInitialisedAction({
            pageOffsets: getPageOffsets(container.item_count),
          }),
        );
        const items$ =
          container.type === 'lists'
            ? this.getReadingListItems(container as ReadingListDetails)
            : this.getSectionItems(container as Section);
        const itemsLoaded$ = items$.pipe(
          distinctUntilChanged(),
          map(pageableItems => new ItemsLoadedAction(pageableItems)),
          catchError(error =>
            observableOf(
              new ItemPageLoadingFailed({
                error: error.error,
                pageOffset: getPageOffsetFromError(error),
              }),
            ),
          ),
        );
        return concat(itemPageReset$, itemsLoaded$);
      }),
    ),
  );

  itemPageLoaded$: Observable<ItemPageLoadedAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(ListItemActionTypes.ITEMS_LOADED),
      map(
        (action: ItemsLoadedAction) => new ItemPageLoadedAction(action.payload),
      ),
    ),
  );

  extractNonGoogleOnlineResources$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ListItemActionTypes.ITEMS_LOADED),
      map((action: ItemsLoadedAction) => {
        return (action.payload as PageableItems).items;
      }),
      map(items => this.partitionOnlineResources(items)),
      filter(hasNonGoogleResources),
      map(getNonGoogleResources),
      map(onlineResources => new OnlineResourcesLoadedAction(onlineResources)),
    ),
  );

  extractGoogleOnlineResources$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ListItemActionTypes.ITEMS_LOADED),
      map((action: ItemsLoadedAction) => {
        return (action.payload as PageableItems).items;
      }),
      map(items => this.partitionOnlineResources(items)),
      filter(hasGoogleResources),
      map(getGoogleResources),
      map(googleResources => new RequestGoogleBooksAction(googleResources)),
    ),
  );

  loadGoogleOnlineResourceForNewlyAddedResource$: Observable<Action> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(ContainerEditActionTypes.CONFIRM_CREATE_RESOURCE_ITEM),
        filter((action: ConfirmCreateResourceItemAction) =>
          isOnlineResourcePreview(
            action.payload.resource.defaultOnlineResource,
          ),
        ),
        map((action: ConfirmCreateResourceItemAction) =>
          List([
            {
              ...action.payload.resource.defaultOnlineResource,
              item_id: action.payload.id,
            } as OnlineResourceGoogleBooks,
          ]),
        ),
        map(googleResources => new RequestGoogleBooksAction(googleResources)),
      ),
    );

  getSectionItems(section: Section): Observable<PageableItems> {
    const total = section.item_count ? section.item_count : 0;
    if (total === 0) {
      return observableOf(PAGEABLE_EMPTY_ITEMS);
    }

    return this.readingListService.getSectionItemsInPages(
      section.id,
      total,
      section.list.is_draft,
    );
  }

  getReadingListItems(
    readingList: ReadingListDetails,
  ): Observable<PageableItems> {
    const listId = readingList.id;
    const total = readingList.item_count ? readingList.item_count : 0;
    const isDraft = readingList.is_draft ? true : false;

    if (total === 0) {
      return observableOf(PAGEABLE_EMPTY_ITEMS);
    }

    return this.readingListService.getReadingListItemsInPages(
      listId,
      total,
      isDraft,
    );
  }

  getSectionItemCitationsByItemId(
    section: Section,
    style: string,
  ): Observable<CitationsByItem> {
    const sectionId = section.id;
    const total = section.item_count ? section.item_count : 0;
    const isDraft = section.list.is_draft ? true : false;

    return this.readingListService.getSectionItemCitationsByItemId(
      sectionId,
      isDraft,
      style,
      total,
    );
  }

  getReadingListItemCitationsByItemId(
    readingList: ReadingListDetails,
    style: string,
  ): Observable<CitationsByItem> {
    const listId = readingList.id;
    const total = readingList.item_count ? readingList.item_count : 0;
    const isDraft = readingList.is_draft ? true : false;

    return this.readingListService.getReadingListItemCitationsByItemId(
      listId,
      isDraft,
      style,
      total,
    );
  }

  partitionOnlineResources(
    items: ListItem[],
  ): Map<boolean, List<OnlineResource>> {
    const itemsList = List(items);

    return itemsList
      .filter(item => item.defaultOnlineResource)
      .map(item => {
        return Object.assign({}, item.defaultOnlineResource, {
          item_id: item.id,
        });
      })
      .groupBy(item => isOnlineResourcePreview(item))
      .toMap() as Map<boolean, List<OnlineResource>>;
  }
}
